
import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';

export default function KitchenOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'pending' | 'cooking'>('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processingOrders, setProcessingOrders] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch orders via edge function to avoid RLS issues
  const fetchOrders = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const result = await response.json();
      setOrders(result.orders || []);
      
      if (showRefreshIndicator) {
        // Show success feedback
        setTimeout(() => {
          setRefreshing(false);
        }, 500);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      // Fallback to empty array if fetch fails
      setOrders([]);
      if (showRefreshIndicator) setRefreshing(false);
    } finally {
      if (!showRefreshIndicator) setLoading(false);
    }
  };

  // Manual refresh handler
  const handleRefresh = () => {
    fetchOrders(true);
  };

  // Real-time updates via polling instead of subscription to avoid RLS issues
  useEffect(() => {
    fetchOrders();
    
    // Poll every 3 seconds for updates
    const interval = setInterval(() => fetchOrders(), 3000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const statusConfig = {
    pending: { 
      label: 'Menunggu Konfirmasi', 
      color: 'bg-yellow-500', 
      textColor: 'text-yellow-400',
      bgColor: 'bg-yellow-900/20',
      borderColor: 'border-yellow-500/30'
    },
    confirmed: { 
      label: 'Dikonfirmasi', 
      color: 'bg-orange-500', 
      textColor: 'text-orange-400',
      bgColor: 'bg-orange-900/20',
      borderColor: 'border-orange-500/30'
    },
    preparing: { 
      label: 'Sedang Dimasak', 
      color: 'bg-blue-500', 
      textColor: 'text-blue-400',
      bgColor: 'bg-blue-900/20',
      borderColor: 'border-blue-500/30'
    },
    ready: { 
      label: 'Siap Disajikan', 
      color: 'bg-green-500', 
      textColor: 'text-green-400',
      bgColor: 'bg-green-900/20',
      borderColor: 'border-green-500/30'
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    // Add order to processing state
    setProcessingOrders(prev => [...prev, orderId]);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId,
          status: newStatus,
          updatedAt: new Date().toISOString()
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      const result = await response.json();
      if (result.success) {
        // Update local state immediately for better UX
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, status: newStatus, updated_at: new Date().toISOString() }
              : order
          )
        );
        
        // Show success message
        const statusMessages = {
          'preparing': 'Pesanan mulai dimasak!',
          'ready': 'Pesanan siap disajikan!',
          'confirmed': 'Pesanan dikonfirmasi!'
        };
        setSuccessMessage(statusMessages[newStatus as keyof typeof statusMessages] || 'Status berhasil diupdate!');
        
        // Refresh data in background
        setTimeout(() => fetchOrders(), 500);
      } else {
        throw new Error(result.error || 'Gagal mengupdate status pesanan');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(`Terjadi kesalahan: ${error.message}`);
    } finally {
      // Remove from processing state
      setProcessingOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  // Enhanced start cooking function with better feedback
  const startCooking = async (orderId: string) => {
    await updateOrderStatus(orderId, 'preparing');
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const getEstimatedTime = (items: any[]) => {
    // Estimasi waktu berdasarkan jenis dan jumlah item
    let totalTime = 0;
    items.forEach(item => {
      if (item.name.toLowerCase().includes('ramen')) totalTime += 8 * item.quantity;
      else if (item.name.toLowerCase().includes('sushi')) totalTime += 5 * item.quantity;
      else if (item.name.toLowerCase().includes('tempura')) totalTime += 6 * item.quantity;
      else if (item.name.toLowerCase().includes('gyoza')) totalTime += 4 * item.quantity;
      else totalTime += 3 * item.quantity;
    });
    return Math.max(totalTime, 5); // Minimum 5 menit
  };

  const filteredOrders = orders.filter(order => {
    // Filter pesanan untuk dapur (pending, confirmed, preparing, ready)
    const kitchenStatuses = ['pending', 'confirmed', 'preparing', 'ready'];
    if (!kitchenStatuses.includes(order.status)) return false;
    
    if (activeFilter === 'all') return true;
    if (activeFilter === 'cooking') return order.status === 'preparing';
    return order.status === activeFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-red-500 text-4xl mb-4 w-12 h-12 flex items-center justify-center mx-auto animate-spin"></i>
          <p className="text-gray-300">Memuat pesanan dapur...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-6 right-6 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-2xl shadow-2xl z-50 flex items-center space-x-3">
          <i className="ri-check-line w-5 h-5 flex items-center justify-center"></i>
          <span className="font-semibold">{successMessage}</span>
        </div>
      )}

      {/* Header & Filters */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Antrian Pesanan Dapur</h2>
          <p className="text-gray-300">Monitor dan kelola pesanan yang perlu dimasak</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Filter Buttons */}
          <div className="flex items-center space-x-2">
            {[
              { id: 'all', label: 'Semua', count: filteredOrders.length },
              { id: 'pending', label: 'Pending', count: orders.filter(o => ['pending', 'confirmed'].includes(o.status)).length },
              { id: 'cooking', label: 'Cooking', count: orders.filter(o => o.status === 'preparing').length }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeFilter === filter.id
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <span>{filter.label}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  activeFilter === filter.id ? 'bg-white/20' : 'bg-gray-600'
                }`}>
                  {filter.count}
                </span>
              </button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className={`whitespace-nowrap cursor-pointer ${
              refreshing 
                ? 'border-green-500 text-green-500 bg-green-500/10' 
                : 'border-red-500 text-red-500 hover:bg-red-500 hover:text-white focus:ring-red-500'
            }`}
          >
            <i className={`mr-2 w-4 h-4 flex items-center justify-center ${
              refreshing 
                ? 'ri-check-line text-green-500' 
                : 'ri-refresh-line'
            } ${refreshing ? '' : 'hover:rotate-180 transition-transform duration-300'}`}></i>
            {refreshing ? 'Berhasil!' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Kitchen Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-yellow-900/20 to-yellow-800/20 backdrop-blur-sm rounded-2xl border border-yellow-500/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-600 rounded-xl flex items-center justify-center">
              <i className="ri-time-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-yellow-400">{orders.filter(o => ['pending', 'confirmed'].includes(o.status)).length}</div>
              <div className="text-gray-300 text-sm">Menunggu</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 backdrop-blur-sm rounded-2xl border border-blue-500/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <i className="ri-chef-hat-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-400">{orders.filter(o => o.status === 'preparing').length}</div>
              <div className="text-gray-300 text-sm">Dimasak</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-900/20 to-green-800/20 backdrop-blur-sm rounded-2xl border border-green-500/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
              <i className="ri-check-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-green-400">{orders.filter(o => o.status === 'ready').length}</div>
              <div className="text-gray-300 text-sm">Siap</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-900/20 to-red-800/20 backdrop-blur-sm rounded-2xl border border-red-500/20 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
              <i className="ri-fire-line text-white w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <div className="text-xl font-bold text-red-400">
                {filteredOrders.length > 0 ? `${getEstimatedTime(filteredOrders[0]?.items || [])}m` : '0m'}
              </div>
              <div className="text-gray-300 text-sm">Estimasi</div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Queue */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredOrders.map((order, index) => (
          <div
            key={order.id}
            className={`backdrop-blur-sm rounded-3xl border transition-all duration-300 ${
              statusConfig[order.status as keyof typeof statusConfig]?.bgColor || 'bg-gray-900/20'
            } ${
              statusConfig[order.status as keyof typeof statusConfig]?.borderColor || 'border-gray-500/30'
            } ${
              selectedOrders.includes(order.id) 
                ? 'shadow-lg shadow-red-500/20 scale-105' 
                : 'hover:scale-102'
            } ${
              processingOrders.includes(order.id) 
                ? 'ring-2 ring-blue-500/50 animate-pulse' 
                : ''
            }`}
          >
            {/* Order Header */}
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold text-white">#{index + 1}</span>
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.id)}
                      onChange={() => toggleOrderSelection(order.id)}
                      className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{order.order_number || order.id}</h3>
                    <p className="text-gray-400 text-sm">Meja {order.table_number}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-white text-xs font-bold ${
                    statusConfig[order.status as keyof typeof statusConfig]?.color || 'bg-gray-500'
                  }`}>
                    {statusConfig[order.status as keyof typeof statusConfig]?.label || order.status}
                  </span>
                  <p className="text-gray-400 text-xs mt-1">
                    {new Date(order.created_at).toLocaleTimeString('id-ID', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>

              {/* Estimated Time */}
              <div className="flex items-center justify-between bg-gray-900/50 rounded-xl p-3">
                <div className="flex items-center space-x-2">
                  <i className="ri-timer-line text-yellow-400 w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-sm">Estimasi Waktu:</span>
                </div>
                <span className="text-yellow-400 font-bold">{getEstimatedTime(order.items)} menit</span>
              </div>
            </div>

            {/* Order Items */}
            <div className="p-6 border-b border-gray-700">
              <h4 className="text-white font-semibold mb-3 flex items-center">
                <i className="ri-bowl-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                Items untuk Dimasak:
              </h4>
              <div className="space-y-3">
                {order.items.map((item: any, itemIndex: number) => (
                  <div key={itemIndex} className="bg-gray-900/30 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{item.name}</span>
                      <span className="bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                        {item.quantity}x
                      </span>
                    </div>
                    {/* Cooking instructions could be added here */}
                    <div className="flex items-center space-x-2 text-xs text-gray-400">
                      <i className="ri-information-line w-3 h-3 flex items-center justify-center"></i>
                      <span>
                        {item.name.toLowerCase().includes('ramen') ? 'Rebus mie, siapkan kuah, tambahkan topping' :
                         item.name.toLowerCase().includes('sushi') ? 'Potong ikan segar, bentuk nasi, rangkai sushi' :
                         item.name.toLowerCase().includes('tempura') ? 'Celup adonan, goreng hingga crispy' :
                         'Ikuti resep standar kitchen'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {order.notes && (
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-xl">
                  <p className="text-yellow-400 text-sm">
                    <i className="ri-sticky-note-line mr-2 w-4 h-4 flex items-center justify-center inline-flex"></i>
                    <strong>Catatan Khusus:</strong> {order.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Kitchen Actions */}
            <div className="p-6">
              <div className="space-y-3">
                {(order.status === 'pending' || order.status === 'confirmed') && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => startCooking(order.id)}
                    disabled={processingOrders.includes(order.id)}
                    className="w-full whitespace-nowrap cursor-pointer bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500"
                  >
                    {processingOrders.includes(order.id) ? (
                      <>
                        <i className="ri-loader-4-line mr-2 w-4 h-4 flex items-center justify-center animate-spin"></i>
                        Memproses...
                      </>
                    ) : (
                      <>
                        <i className="ri-chef-hat-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        Mulai Memasak
                      </>
                    )}
                  </Button>
                )}

                {order.status === 'preparing' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => updateOrderStatus(order.id, 'ready')}
                    disabled={processingOrders.includes(order.id)}
                    className="w-full whitespace-nowrap cursor-pointer bg-green-600 hover:bg-green-700"
                  >
                    {processingOrders.includes(order.id) ? (
                      <>
                        <i className="ri-loader-4-line mr-2 w-4 h-4 flex items-center justify-center animate-spin"></i>
                        Memproses...
                      </>
                    ) : (
                      <>
                        <i className="ri-check-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        Selesai - Siap Disajikan
                      </>
                    )}
                  </Button>
                )}

                {order.status === 'ready' && (
                  <div className="text-center py-4">
                    <i className="ri-check-double-line text-green-400 text-3xl mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                    <p className="text-green-400 font-semibold text-sm">
                      Pesanan Siap Diambil Kasir
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-chef-hat-line text-gray-500 text-6xl mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
          <h3 className="text-xl font-bold text-gray-400 mb-2">
            {activeFilter === 'all' ? 'Tidak ada pesanan di dapur' : 
             activeFilter === 'pending' ? 'Tidak ada pesanan pending' :
             'Tidak ada pesanan yang sedang dimasak'}
          </h3>
          <p className="text-gray-500">
            {activeFilter === 'all' ? 'Semua pesanan sudah selesai' : 
             'Coba filter lain untuk melihat pesanan'}
          </p>
        </div>
      )}

      {/* Bulk Actions for Kitchen */}
      {selectedOrders.length > 0 && (
        <div className="fixed bottom-6 left-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-4 shadow-2xl">
          <div className="flex items-center space-x-4">
            <span className="text-white font-semibold">
              {selectedOrders.length} pesanan dipilih
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                selectedOrders.forEach(orderId => {
                  const order = orders.find(o => o.id === orderId);
                  if (order && ['pending', 'confirmed'].includes(order.status)) {
                    updateOrderStatus(orderId, 'preparing');
                  } else if (order && order.status === 'preparing') {
                    updateOrderStatus(orderId, 'ready');
                  }
                });
                setSelectedOrders([]);
              }}
              className="whitespace-nowrap cursor-pointer"
            >
              <i className="ri-chef-hat-line mr-2 w-4 h-4 flex items-center justify-center"></i>
              Proses Batch
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedOrders([])}
              className="whitespace-nowrap cursor-pointer border-white text-white hover:bg-white hover:text-blue-600"
            >
              <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
